// Settings, organised by what you are configuring rather than by which
// backend owns it.
//
// The old page was one scrolling column with four tabs bolted on, built around
// a single active store. With stores, profiles, capacity limits and stream
// policy all first-class now, the sections are addressable from a side nav
// that scales as more are added, and each one is a self-contained module.

import {
  Bell,
  Building2,
  Camera as CamIcon,
  Cpu,
  Database,
  Gauge,
  HardDrive,
  Layers,
  Router,
  ScanText,
  Server,
  Settings as SettingsIcon,
  Store as StoreIcon,
  Users,
  Wifi,
} from "lucide-react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { PageHeader } from "../../components/PageHeader";
import { useRole } from "../../hooks/useRole";
import { AlertsSection } from "./AlertsSection";
import { BoxesSection } from "./BoxesSection";
import { CamerasSection } from "./CamerasSection";
import { CapacitySection } from "./CapacitySection";
import { ConnectorsSection } from "./ConnectorsSection";
import { CustomerCamerasSection } from "./CustomerCamerasSection";
import { CustomerRecordersSection } from "./CustomerRecordersSection";
import { CustomersSection } from "./CustomersSection";
import { DataSection } from "./DataSection";
import { DescriptionsSection } from "./DescriptionsSection";
import { GpuWorkersSection } from "./GpuWorkersSection";
import { InternetSection } from "./InternetSection";
import { LocationsSection } from "./LocationsSection";
import { NvrsSection } from "./NvrsSection";
import { PeopleSection } from "./PeopleSection";
import { ProfilesSection } from "./ProfilesSection";
import { StorageSection } from "./StorageSection";
import { StoresSection } from "./StoresSection";
import { StreamingSection } from "./StreamingSection";

type SectionId =
  | "stores"
  | "cameras"
  | "profiles"
  | "nvrs"
  | "connectors"
  | "internet"
  | "customers"
  | "people"
  | "alerts"
  | "descriptions"
  | "streaming"
  | "capacity"
  | "gpus"
  | "storage"
  | "data";

type SectionDef = {
  id: SectionId;
  label: string;
  icon: typeof CamIcon;
  group: string;
  /** Hidden from everyone but IntraScene staff admins. */
  adminOnly?: boolean;
  /** Hidden from everyone but the dev role. */
  devOnly?: boolean;
};

// What IntraScene staff see: the whole fleet, in the system's own terms.
const STAFF_SECTIONS: SectionDef[] = [
  { id: "stores", label: "Stores", icon: StoreIcon, group: "Sites & cameras" },
  { id: "cameras", label: "Cameras", icon: CamIcon, group: "Sites & cameras" },
  { id: "profiles", label: "Camera profiles", icon: Layers, group: "Sites & cameras" },
  { id: "nvrs", label: "Recorders", icon: Server, group: "Sites & cameras" },
  { id: "connectors", label: "Connectors", icon: Router, group: "Sites & cameras" },
  { id: "internet", label: "Internet speed", icon: Wifi, group: "Sites & cameras" },
  {
    id: "customers",
    label: "Customers",
    icon: Building2,
    group: "Accounts",
    adminOnly: true,
  },
  { id: "people", label: "People", icon: Users, group: "Accounts", adminOnly: true },
  { id: "alerts", label: "Alert settings", icon: Bell, group: "Alerts" },
  { id: "descriptions", label: "Descriptions", icon: ScanText, group: "System" },
  { id: "streaming", label: "Stream quality", icon: Gauge, group: "System" },
  { id: "capacity", label: "Capacity & limits", icon: Cpu, group: "System" },
  { id: "gpus", label: "GPU workers", icon: Cpu, group: "System", devOnly: true },
  {
    id: "storage",
    label: "Storage",
    icon: HardDrive,
    group: "System",
    adminOnly: true,
  },
  {
    id: "data",
    label: "Stored data",
    icon: Database,
    group: "System",
    adminOnly: true,
  },
];

// What a customer's owner sees: their own account, in plain words.
const CUSTOMER_SECTIONS: SectionDef[] = [
  { id: "stores", label: "Locations", icon: StoreIcon, group: "Your locations" },
  { id: "cameras", label: "Cameras", icon: CamIcon, group: "Your locations" },
  { id: "profiles", label: "Camera groups", icon: Layers, group: "Your locations" },
  { id: "nvrs", label: "Video recorders", icon: Server, group: "Your locations" },
  { id: "connectors", label: "IntraScene boxes", icon: Router, group: "Your locations" },
  { id: "internet", label: "Internet speed", icon: Wifi, group: "Your locations" },
  { id: "people", label: "People", icon: Users, group: "Your account" },
  { id: "alerts", label: "Alert settings", icon: Bell, group: "Alerts" },
];

export default function Settings() {
  const { isAdmin, isDev, isStaff, canManage, isLoading } = useRole();
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as { section?: string };

  const sections = isStaff
    ? STAFF_SECTIONS
    : canManage
      ? CUSTOMER_SECTIONS
      : CUSTOMER_SECTIONS.filter((s) => s.id === "alerts");
  const permitted = (s: SectionDef) => (!s.adminOnly || isAdmin) && (!s.devOnly || isDev);
  const visible = sections.filter(permitted);
  const groups = [...new Set(visible.map((s) => s.group))];
  const requested = search.section as SectionId | undefined;
  const allowed =
    requested &&
    sections.some(
      (s) => s.id === requested && (permitted(s) || isLoading),
    );
  const active: SectionId = allowed && requested ? requested : (visible[0]?.id ?? "alerts");

  const setActive = (id: SectionId) => {
    nav({ to: "/settings", search: { section: id }, replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        icon={SettingsIcon}
        description={
          !isStaff
            ? "Set up your locations, cameras and the people who use them."
            : isAdmin
              ? "Set up stores, cameras, customers, and how the system works."
              : "See how this system is set up."
        }
      />

      <div className="grid gap-6 lg:grid-cols-[13rem_1fr]">
        <nav className="space-y-4 lg:sticky lg:top-0 lg:self-start" aria-label="Settings sections">
          {groups.map((group) => (
            <div key={group}>
              <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-bf-muted">
                {group}
              </p>
              <div className="space-y-0.5">
                {visible
                  .filter((s) => s.group === group)
                  .map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActive(id)}
                      aria-current={active === id ? "page" : undefined}
                      className={
                        "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition " +
                        (active === id
                          ? "bg-brand-purple/15 text-white ring-1 ring-inset ring-brand-purple/30"
                          : "text-bf-muted hover:bg-white/5 hover:text-bf-text")
                      }
                    >
                      <Icon size={16} className="shrink-0" />
                      {label}
                    </button>
                  ))}
              </div>
            </div>
          ))}
        </nav>

        {isLoading ? (
          <div className="bf-card text-sm text-bf-muted">Loading…</div>
        ) : isStaff ? (
          <div className="min-w-0">
            {active === "stores" && <StoresSection canEdit={canManage} />}
            {active === "cameras" && <CamerasSection canEdit={canManage} />}
            {active === "profiles" && <ProfilesSection canEdit={canManage} />}
            {active === "nvrs" && <NvrsSection canEdit={canManage} />}
            {active === "connectors" && <ConnectorsSection canEdit={canManage} />}
            {active === "internet" && <InternetSection canEdit={canManage} />}
            {active === "customers" && isAdmin && <CustomersSection />}
            {active === "people" && isAdmin && <PeopleSection />}
            {active === "alerts" && <AlertsSection />}
            {active === "descriptions" && <DescriptionsSection isAdmin={isAdmin} />}
            {active === "streaming" && <StreamingSection isAdmin={isAdmin} />}
            {active === "capacity" && <CapacitySection />}
            {active === "gpus" && isDev && <GpuWorkersSection />}
            {active === "storage" && isAdmin && <StorageSection />}
            {active === "data" && isAdmin && <DataSection />}
          </div>
        ) : (
          <div className="min-w-0">
            {active === "stores" && <LocationsSection canEdit={canManage} />}
            {active === "cameras" && <CustomerCamerasSection canEdit={canManage} />}
            {active === "profiles" && <ProfilesSection canEdit={canManage} />}
            {active === "nvrs" && <CustomerRecordersSection canEdit={canManage} />}
            {active === "connectors" && <BoxesSection canEdit={canManage} />}
            {active === "internet" && <InternetSection canEdit={canManage} />}
            {active === "people" && canManage && <PeopleSection />}
            {active === "alerts" && <AlertsSection />}
          </div>
        )}
      </div>
    </div>
  );
}
